import { FormRadioGroup, FormSectionHeader, FormSelect, Loader } from "@/components"
import { Info, Play } from "lucide-react";
import { Button, Card, CardContent } from "@/components/ui";
import {
  ACC_CATEGORY, BUDGET, BUDGET_VERSION, budgetOptions, DOCUMENT_TYPES, DOWNLOAD_REPORT, downloadReportOptions, FINANCIAL_VERSION,
  FISCAL_YEAR, GL_MASTERS, OBJECT_TYPE, OBJECT_VALUES, PERIOD_FROM, PERIOD_TO, REPORT_EXPANSION, reportExpansionOptions,
  REPORTING_LEVEL, REPORTING_PERIOD, reportingPeriodOptions, RETAINED_EARNING, retainedEarningsOptions, SCOPE, scopeOptions,
  UNIT_DISPLAY, unitDisplayOptions,
} from "../consts";
import { reportingLevelOptions, yearOptions } from "@/lib/utils";
import { FormProvider } from "react-hook-form";

const periods = Array.from({ length: 12 }, (_, i) => ({ label: (i + 1).toString().padStart(2, "0"), value: (i + 1).toString() }));

/** Managerial Reporting criteria (Managerial Report.docx, screens 1-2). */
const FormView = ({
  isLoading,
  isRunning,
  onReset,
  onLoadVariant,
  onSaveBtnClick,
  form,
  onSubmit,
  objectValues,
  isLoadingDropdown,
  formData,
  docTypeList,
  glMasterList,
  financialVersionOptions,
  budgetVersionOptions,
  accountCategoryOptions,
  reportLevelOptions,
}: any) => {
  return (
    <>
      {isLoading ? <Loader /> :
        <div className="flex flex-col gap-5">
          <div className="flex flex-row items-center gap-3 text-[14px] bg-blue-50 border-blue-100 border rounded-md p-4">
            <Info className="h-5 w-5 shrink-0"/>
            <span>Configure the criteria below, then run the report. Save any combination as a named variant to reuse it later.</span>
          </div>

          <Card className="rounded-md shadow-none p-0">
            <CardContent className="flex flex-col md:flex-row md:items-center md:justify-end gap-3 p-3">
              <Button type="button" variant="outline" onClick={onReset}>Reset</Button>
              <Button type="button" onClick={onLoadVariant}>Load Variant</Button>
              <Button type="button" onClick={onSaveBtnClick}>Save Variant</Button>
            </CardContent>
          </Card>

          <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <Card className="rounded-md shadow-none">
                <CardContent className="grid grid-cols-1 gap-10">
                  <div className="flex flex-col md:flex-row md:justify-between gap-4">
                    <FormSectionHeader className="flex flex-col text-primary">
                      Report Options
                      <span className="text-[12px] text-gray-700 font-normal">These drive how this report is built and displayed.</span>
                    </FormSectionHeader>
                    <div className="w-full md:w-[340px]">
                      <FormSelect
                        name={FINANCIAL_VERSION}
                        control={form.control}
                        label="Financial Version"
                        options={financialVersionOptions}
                        placeholder={financialVersionOptions.length ? "Please select" : "No published version"}
                      />
                      {!financialVersionOptions.length && (
                        <p className="mt-1 text-xs text-amber-700">Publish a version under Master Data › Financial Statement Versions.</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <FormSectionHeader wrapperClassName="mb-2">Report Expansion</FormSectionHeader>
                      <FormRadioGroup name={REPORT_EXPANSION} control={form.control} options={reportExpansionOptions} />
                    </div>
                    <div>
                      <FormSectionHeader wrapperClassName="mb-2">Scope</FormSectionHeader>
                      <FormRadioGroup name={SCOPE} control={form.control} options={scopeOptions} />
                    </div>
                    <div>
                      <FormSectionHeader wrapperClassName="mb-2">Unit Display</FormSectionHeader>
                      <FormRadioGroup name={UNIT_DISPLAY} control={form.control} options={unitDisplayOptions} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div>
                      <FormSectionHeader wrapperClassName="mb-2">Retained Earnings</FormSectionHeader>
                      <FormRadioGroup name={RETAINED_EARNING} control={form.control} options={retainedEarningsOptions} />
                    </div>
                    <div>
                      <FormSectionHeader wrapperClassName="mb-2">Budgets</FormSectionHeader>
                      <FormRadioGroup name={BUDGET} control={form.control} options={budgetOptions} />
                    </div>
                    {formData[BUDGET] == "WITH_BUDGET" && (
                      <div>
                        <FormSelect
                          name={BUDGET_VERSION}
                          control={form.control}
                          options={budgetVersionOptions}
                          label="Budget Version"
                          placeholder={budgetVersionOptions.length ? "Please select" : `No approved budget for ${formData[FISCAL_YEAR] || "this year"}`}
                        />
                      </div>
                    )}
                  </div>

                  <div className="border-b border-gray-200 pb-6">
                    <FormSectionHeader wrapperClassName="mb-2">Download Report</FormSectionHeader>
                    <FormRadioGroup name={DOWNLOAD_REPORT} control={form.control} options={downloadReportOptions} />
                  </div>

                  <div>
                    <FormSectionHeader>Object Selection</FormSectionHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      <FormSelect name={OBJECT_TYPE} control={form.control} options={reportingLevelOptions} label="Object Type" />
                      <FormSelect
                        name={OBJECT_VALUES}
                        control={form.control}
                        options={objectValues}
                        label="Object Values"
                        multiSelect
                        showChip
                        placeholder={formData[OBJECT_TYPE] == "COMMUNITY" ? "" : "All (or pick some)"}
                        disabled={isLoadingDropdown || formData[OBJECT_TYPE] == "COMMUNITY"}
                      />
                      <FormSelect name={REPORTING_LEVEL} control={form.control} options={reportLevelOptions} label="Report Level" />
                    </div>
                  </div>

                  <div>
                    <FormSectionHeader>Reporting Period</FormSectionHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-center">
                      <FormRadioGroup name={REPORTING_PERIOD} control={form.control} options={reportingPeriodOptions} />
                      <FormSelect
                        name={FISCAL_YEAR}
                        control={form.control}
                        options={yearOptions(7)?.map((d: any) => ({ value: String(d.value), label: d.label }))}
                        label="Fiscal Year"
                      />
                      <FormSelect name={PERIOD_FROM} control={form.control} options={periods} label="Period From" />
                      <FormSelect
                        name={PERIOD_TO}
                        control={form.control}
                        options={periods.map((p) => ({ ...p, disabled: Number(p.value) < Number(formData[PERIOD_FROM]) }))}
                        label="Period To"
                      />
                    </div>
                  </div>

                  <div>
                    <FormSectionHeader>Additional Filters</FormSectionHeader>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      <FormSelect
                        name={DOCUMENT_TYPES}
                        control={form.control}
                        multiSelect
                        showChip
                        placeholder="All"
                        options={docTypeList?.map((data: any) => ({
                          label: `${data?.documentType} - ${data?.documentTypeDisplayName ?? data?.description ?? ""}`,
                          value: data?.documentTypeId?.toString(),
                        }))}
                        label="Document Types"
                      />
                      <FormSelect
                        name={GL_MASTERS}
                        control={form.control}
                        multiSelect
                        showChip
                        placeholder="All"
                        options={glMasterList?.map((data: any) => ({
                          label: `${data?.glAccountNumber} - ${data?.accountShortDesc}`,
                          value: data?.glMasterId?.toString(),
                        }))}
                        label="GL Accounts"
                      />
                      <FormSelect
                        name={ACC_CATEGORY}
                        control={form.control}
                        multiSelect
                        showChip
                        placeholder="All"
                        options={accountCategoryOptions}
                        label="Account Categories"
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-end">
                    <Button type="submit" disabled={isRunning}>
                      {isRunning ? <Loader variant="secondary" /> : <Play className="w-4 h-4" />} Run Report
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </form>
          </FormProvider>
        </div>
      }
    </>
  )
}

export default FormView;
